import React, { useEffect, useRef } from 'react';
import {
  Chart,
  DoughnutController,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { toRGBA } from './utils';

// Register Chart.js components
Chart.register(
  DoughnutController,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export interface DoughnutChartProps {
  /** Data values */
  data: number[];
  /** Labels for each segment */
  labels: string[];
  /** Colors for each segment */
  colors: string[];
  /** Container height in pixels */
  height?: number;
  /** Whether to show legend */
  showLegend?: boolean;
  /** Legend position */
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** Chart title */
  title?: string;
  /** Cutout percentage (for donut hole) */
  cutout?: string;
}

export function DoughnutChart({
  data,
  labels,
  colors,
  height = 250,
  showLegend = true,
  legendPosition = 'bottom',
  title,
  cutout = '60%',
}: DoughnutChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const backgroundColors = colors.map(c => toRGBA(c, 0.85));
    const borderColors = colors;

    chartInstance.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout,
        plugins: {
          legend: {
            display: showLegend,
            position: legendPosition,
          },
          title: title ? { display: true, text: title } : undefined,
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                return `${label}: ${value} (${percentage}%)`;
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, labels, colors, showLegend, legendPosition, title, cutout]);

  return (
    <div style={{ height }}>
      <canvas ref={chartRef} />
    </div>
  );
}

export default DoughnutChart;
